import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { MaskLines, EASE } from "./Reveal";
import { Link } from "react-router-dom";

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-3d-visual">
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-[28px] bg-ink shadow-[0_40px_80px_-30px_rgba(15,23,42,0.55)]"
      >
        <motion.img
          src="/img/hero.jpg"
          alt="Iridescent glass sculpture"
          initial={{ scale: 1.35 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.2, ease: EASE, delay: 0.5 }}
          className="h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.25),transparent_45%)]" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white backdrop-blur-xl">
          <span className="font-mono text-xs uppercase tracking-[0.18em]">Model · in production</span>
          <span className="flex items-center gap-2 font-mono text-xs"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald" />live</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay: 1.4 }}
        className="absolute -left-6 -top-6 hidden h-28 w-28 sm:block"
      >
        <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
          <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <circle cx="50" cy="50" r="50" fill="#F59E0B" />
          <text className="fill-ink font-mono text-[9.5px] uppercase tracking-[0.2em]">
            <textPath href="#circ">Applied AI · Data · Automation ·</textPath>
          </text>
        </svg>
        <Sparkles className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-ink" />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-20 pt-32 sm:pt-40 lg:pb-28">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[620px] w-[620px] rounded-full bg-violet/20 blur-[120px]" />
      <div className="pointer-events-none absolute -left-32 top-1/2 h-[420px] w-[420px] rounded-full bg-amber/25 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-end gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:px-8">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] text-ink"
          >
            <span className="h-2 w-2 rounded-full bg-coral" /> Clevanta Technologies
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display text-[3.4rem] leading-[0.95] tracking-tight text-ink sm:text-7xl lg:text-[7.2rem]">
            <MaskLines
              delay={0.35}
              lines={[
                <>Intelligence,</>,
                <>engineered <em className="text-violet">for</em></>,
                <>what <span className="relative inline-block">comes<svg viewBox="0 0 300 20" className="absolute -bottom-2 left-0 w-full" preserveAspectRatio="none"><motion.path d="M2 14 Q 150 -4 298 12" fill="none" stroke="#FF4D4D" strokeWidth="6" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, delay: 1.4, ease: EASE }} /></svg></span> next.</>,
              ]}
            />
          </h1>
          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1 }}
            className="mt-10 max-w-xl text-base leading-relaxed text-ink/75 md:text-lg"
          >
            We design and ship generative AI products, LLM applications, data platforms and custom machine learning — built to run in production, not just in a demo.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" data-testid="hero-cta-button" className="group inline-flex items-center gap-3 rounded-full bg-ink py-2 pl-7 pr-2 text-base font-semibold text-[#FBF9F5] transition-colors hover:bg-violet">
              Book a discovery call
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-coral transition-transform duration-500 group-hover:rotate-45">
                <ArrowUpRight className="h-5 w-5" />
              </span>
            </Link>
            <a href="#services" data-testid="hero-secondary-link" className="link-underline text-base font-semibold text-ink">
              Explore services
            </a>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} className="mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};
